import { isBrowser } from '@sbt-web/utils';
import logger from '../server/logger';
import type { UserFeedbackResponse } from '../types';

const trustFeedbackTimeoutMs = isBrowser() ? 5000 : 1000;
const aidosBaseUrl =
  process.env.AIDOS_FEEDBACK_BASE_URL ||
  process.env.NEXT_PUBLIC_AIDOS_FEEDBACK_BASE_URL;

/**
 * Get the feedbacks for a user
 * @param userId The ID of the user who recieved the feedback
 * @param limit The page size of the results
 * @param page The page number (zero-based)
 * @param requestID The existing request ID, to be used server-side
 */
export const getUserFeedbacks = async (
  userId: string,
  limit: number,
  page: number,
  switchToAidos: boolean,
  requestID?: string
): Promise<UserFeedbackResponse | undefined> => {
  try {
    const feedbackQuery = new URLSearchParams({
      limit: limit.toString(),
      page: page.toString(),
      // We specifically only and always want to get feedback from members.
      // We do not want to consider automatic reviews.
      sources: 'MEMBER',
    });

    let timeoutSignal: AbortSignal;

    // Feature test: on some of our supported browsers, the AbortSignal.timeout
    // is not available so we use the fallback with a setTimeout.
    if ('timeout' in AbortSignal) {
      timeoutSignal = AbortSignal.timeout(trustFeedbackTimeoutMs);
    } else {
      const cancellationController = new AbortController();
      setTimeout(() => {
        cancellationController.abort(
          'The request to Trust feedback api has timed out.'
        );
      }, trustFeedbackTimeoutMs);

      timeoutSignal = cancellationController.signal;
    }

    const trustBaseUrl: string = switchToAidos
      ? aidosBaseUrl
      : process.env.NEXT_PUBLIC_TRUST_FEEDBACK_BASE_URL;

    const getFeedbackResponse = await fetch(
      `${trustBaseUrl}/public/users/sdrn:subito:user:${userId}/feedback?${feedbackQuery.toString()}`,
      {
        method: 'GET',
        signal: timeoutSignal,
      }
    );

    if (getFeedbackResponse.ok) {
      return await getFeedbackResponse.json();
    }

    return undefined;
  } catch (e) {
    logger.logError(
      `Get user feedbacks for user ${userId} failed. Error: ${e}`,
      requestID
    );

    return undefined;
  }
};
